import { useState } from 'react';

interface AlterarSenhaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSucesso: () => void;
}

export default function AlterarSenhaModal({ isOpen, onClose, onSucesso }: AlterarSenhaModalProps) {
  const [etapa, setEtapa] = useState<'codigo' | 'nova_senha'>('codigo');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarConfirmar, setMostrarConfirmar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validarSenha = (senha: string) => {
    const requisitos = {
      minLength: senha.length >= 8,
      hasUpper: /[A-Z]/.test(senha),
      hasLower: /[a-z]/.test(senha),
      hasNumber: /\d/.test(senha),
    };
    return requisitos;
  };

  const requisitos = validarSenha(novaSenha);
  const senhaValida = Object.values(requisitos).every(Boolean);

  const handleConfirmarCodigo = () => {
    if (codigo.length !== 6) {
      setErros({ codigo: 'Informe o código de 6 dígitos' });
      return;
    }
    if (codigo !== '123456') {
      setErros({ codigo: 'Código inválido. Tente novamente.' });
      return;
    }
    setErros({});
    setEtapa('nova_senha');
  };

  const handleSalvarSenha = () => {
    const novosErros: Record<string, string> = {};
    if (!senhaValida) {
      novosErros.novaSenha = 'A senha não atende aos requisitos mínimos';
    }
    if (novaSenha !== confirmarSenha) {
      novosErros.confirmarSenha = 'As senhas não coincidem';
    }
    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      return;
    }
    setSalvando(true);
    setTimeout(() => {
      setSalvando(false);
      handleFechar();
      onSucesso();
    }, 1500);
  };

  const handleFechar = () => {
    setEtapa('codigo');
    setCodigo('');
    setNovaSenha('');
    setConfirmarSenha('');
    setErros({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <i className="ri-lock-password-line text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold">Alterar Senha</h3>
                <p className="text-xs text-slate-300 mt-0.5">
                  {etapa === 'codigo' ? 'Etapa 1 de 2 — Verificação' : 'Etapa 2 de 2 — Nova senha'}
                </p>
              </div>
            </div>
            <button
              onClick={handleFechar}
              className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg hover:bg-white/30 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          {/* Progress bar */}
          <div className="flex gap-1.5 mt-4">
            <div className="h-1 flex-1 rounded-full bg-orange-400"></div>
            <div className={`h-1 flex-1 rounded-full transition-colors ${etapa === 'nova_senha' ? 'bg-orange-400' : 'bg-white/20'}`}></div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {etapa === 'codigo' && (
            <>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                <i className="ri-mail-send-line text-3xl text-orange-500 mb-2"></i>
                <p className="text-sm font-semibold text-slate-900 mb-1">Código enviado!</p>
                <p className="text-xs text-slate-600">
                  Enviamos um código de 6 dígitos para<br />
                  <strong>carlos.silva@empresa.com</strong>
                </p>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">Código de Verificação</label>
                <input
                  type="text"
                  value={codigo}
                  onChange={(e) => {
                    setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setErros({});
                  }}
                  placeholder="000000"
                  maxLength={6}
                  className={`w-full px-4 py-3 border rounded-lg text-center text-2xl font-bold tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                    erros.codigo ? 'border-red-400' : 'border-slate-300'
                  }`}
                />
                {erros.codigo && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {erros.codigo}
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Use <strong>123456</strong> para testar
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={handleFechar} className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors cursor-pointer text-sm">
                  Cancelar
                </button>
                <button onClick={handleConfirmarCodigo} className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer text-sm">
                  Continuar
                </button>
              </div>
            </>
          )}

          {etapa === 'nova_senha' && (
            <>
              {/* Nova senha */}
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">Nova Senha</label>
                <div className="relative">
                  <input
                    type={mostrarSenha ? 'text' : 'password'}
                    value={novaSenha}
                    onChange={(e) => { setNovaSenha(e.target.value); setErros({}); }}
                    placeholder="Digite a nova senha"
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${
                      erros.novaSenha ? 'border-red-400' : 'border-slate-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(!mostrarSenha)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <i className={mostrarSenha ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                {erros.novaSenha && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {erros.novaSenha}
                  </p>
                )}
              </div>

              {/* Requisitos */}
              {novaSenha.length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                  {[
                    { ok: requisitos.minLength, label: 'Mínimo 8 caracteres' },
                    { ok: requisitos.hasUpper, label: 'Uma letra maiúscula' },
                    { ok: requisitos.hasLower, label: 'Uma letra minúscula' },
                    { ok: requisitos.hasNumber, label: 'Um número' },
                  ].map((req, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${req.ok ? 'bg-green-500' : 'bg-slate-300'}`}>
                        <i className={`text-white text-[10px] ${req.ok ? 'ri-check-line' : 'ri-close-line'}`}></i>
                      </div>
                      <span className={`text-xs ${req.ok ? 'text-green-700' : 'text-slate-500'}`}>{req.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Confirmar senha */}
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">Confirmar Nova Senha</label>
                <div className="relative">
                  <input
                    type={mostrarConfirmar ? 'text' : 'password'}
                    value={confirmarSenha}
                    onChange={(e) => { setConfirmarSenha(e.target.value); setErros({}); }}
                    placeholder="Repita a nova senha"
                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm ${
                      erros.confirmarSenha ? 'border-red-400' : confirmarSenha && confirmarSenha === novaSenha ? 'border-green-400' : 'border-slate-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarConfirmar(!mostrarConfirmar)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                  >
                    <i className={mostrarConfirmar ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
                {erros.confirmarSenha && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {erros.confirmarSenha}
                  </p>
                )}
                {confirmarSenha && confirmarSenha === novaSenha && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <i className="ri-check-line"></i>
                    Senhas coincidem
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button onClick={() => setEtapa('codigo')} className="flex-1 px-4 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors cursor-pointer text-sm">
                  Voltar
                </button>
                <button
                  onClick={handleSalvarSenha}
                  disabled={salvando}
                  className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors cursor-pointer text-sm flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {salvando ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Salvando...</>
                  ) : (
                    <><i className="ri-lock-line"></i> Salvar Senha</>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
